import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Download, MonitorDown, Apple, Server as ServerIcon, ShieldCheck, CheckCircle2 } from "lucide-react";

// PUBLIC give-out page (no login). The operator shares /install?token=<t>; the
// client opens it, picks their OS, and installs with one command or the direct
// download. The token is the scoped credential — validated server-side.

const PLATFORMS = [
  { key: "windows", label: "Windows", icon: MonitorDown, hint: "Windows 10 / 11 · Server 2016+", dl: "windows" },
  { key: "macos", label: "macOS", icon: Apple, hint: "Apple Silicon or Intel", dl: "macos_arm64" },
  { key: "linux", label: "Linux", icon: ServerIcon, hint: "Ubuntu · Debian · RHEL", dl: "linux" },
];

function useQueryToken() {
  const [t, setT] = useState("");
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    setT(p.get("token") || "");
  }, []);
  return t;
}

export default function InstallPublic() {
  const token = useQueryToken();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | invalid | notoken
  const [platform, setPlatform] = useState("windows");

  useEffect(() => {
    if (token === "") return; // wait for parse
    if (!token) { setState("notoken"); return; }
    api.get("/agents/install-info", { params: { token } })
      .then(r => { setInfo(r.data); setState("ok"); })
      .catch(() => setState("invalid"));
    // detect OS for a sensible default
    const ua = navigator.userAgent || "";
    if (/Mac/i.test(ua)) setPlatform("macos");
    else if (/Linux/i.test(ua) && !/Android/i.test(ua)) setPlatform("linux");
  }, [token]);

  const cmd = info?.install_commands?.[platform];
  const dlKey = PLATFORMS.find(p => p.key === platform)?.dl;
  const dl = info?.downloads?.[dlKey];

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-200 flex flex-col items-center px-4 py-10">
      <div className="w-full max-w-xl">
        {/* brand */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg" style={{ background: "linear-gradient(145deg,#38d6f5,#2046e8)" }} />
          <div>
            <div className="text-lg font-semibold text-zinc-50">AegisOne RMM</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-400 font-mono">Endpoint Agent Setup</div>
          </div>
        </div>

        {state === "loading" && <div className="text-zinc-500 text-sm">Checking your install link…</div>}

        {state === "notoken" && (
          <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-6">
            <div className="text-zinc-200 font-medium mb-1">No install link</div>
            <div className="text-sm text-zinc-500">Ask your IT provider for the installer link they generated for you.</div>
          </div>
        )}

        {state === "invalid" && (
          <div className="bg-zinc-950 border border-red-500/30 rounded-lg p-6">
            <div className="text-red-300 font-medium mb-1">This link is invalid or has expired</div>
            <div className="text-sm text-zinc-500">Ask your IT provider for a fresh installer link.</div>
          </div>
        )}

        {state === "ok" && info && (
          <>
            <div className="text-sm text-zinc-400 mb-5">
              Install the management agent on this device. It lets your IT provider keep the machine
              secure, patched and supported. Choose your operating system:
            </div>

            {/* OS picker */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              {PLATFORMS.map(p => {
                const Icon = p.icon;
                const on = platform === p.key;
                return (
                  <button key={p.key} onClick={() => setPlatform(p.key)}
                    className={`rounded-lg p-3 border text-center transition-colors ${on ? "bg-blue-500/15 border-blue-500/40" : "bg-zinc-950 border-zinc-900 hover:border-zinc-800"}`}>
                    <Icon size={22} className={`mx-auto mb-1.5 ${on ? "text-blue-300" : "text-zinc-400"}`} />
                    <div className={`text-sm ${on ? "text-blue-200" : "text-zinc-300"}`}>{p.label}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5">{p.hint}</div>
                  </button>
                );
              })}
            </div>

            {/* one-line install */}
            <div className="bg-zinc-950 border border-zinc-900 rounded-lg p-4 mb-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
                Recommended · run this {platform === "windows" ? "in PowerShell (as Administrator)" : "in Terminal"}
              </div>
              <div className="flex items-start gap-2">
                <code className="flex-1 bg-black/40 border border-zinc-900 rounded p-2.5 font-mono text-[11.5px] break-all text-zinc-300">{cmd}</code>
                <button onClick={() => { navigator.clipboard.writeText(cmd); toast.success("Copied"); }}
                  className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-2.5 py-2 text-zinc-300 shrink-0"><Copy size={14} /></button>
              </div>
            </div>

            {/* manual download */}
            <div className="flex items-center gap-3">
              <a href={dl} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded px-3.5 py-2 text-sm text-zinc-300">
                <Download size={15} /> Or download the installer
              </a>
              {platform === "macos" && (
                <a href={info.downloads?.macos_amd64} className="text-xs text-zinc-500 hover:text-zinc-300">Intel Mac?</a>
              )}
            </div>

            <div className="flex items-start gap-2 text-[11px] text-zinc-600 mt-6 border-t border-zinc-900 pt-4">
              <ShieldCheck size={14} className="text-zinc-500 shrink-0 mt-0.5" />
              <span>The agent connects outbound over an encrypted channel. It reports device health, inventory and security posture to your provider. {info.batch ? "" : "This is a single-use link."}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-emerald-500/80 mt-2">
              <CheckCircle2 size={13} /> Link verified{info.expires_at ? ` · valid until ${info.expires_at.slice(0, 10)}` : ""}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
